/**
 * Authority Assets — internal strategy dashboard content for /admin/seo.
 *
 * This is STRATEGY ONLY. No outreach is performed by this dashboard,
 * no external accounts are created, no links are purchased, no
 * backlink data is fabricated. Every recommended asset reflects
 * actual internal content status.
 */

export const AUTHORITY_ASSETS = [
  {
    name: "India Manufacturing Cluster Guides",
    audience: "International retail buyers, importers, sourcing teams",
    intent: "Informational · Commercial",
    theme: "India manufacturing clusters",
    url: "/clusters",
    contentType: "Editorial hub + 15 detail pages",
    length: "Hub + ~1,500 words per cluster",
    potentialSources: ["Trade publications", "Supply-chain research platforms", "Sourcing associations"],
    authorityPotential: "High",
    status: "Published",
    priority: "high",
  },
  {
    name: "India Sourcing Cost Guide",
    audience: "US / UK / EU buyers evaluating India vs China vs Vietnam",
    intent: "Informational · Commercial",
    theme: "India sourcing landed cost",
    url: "/trends/india-vs-vietnam-sourcing-us-buyers",
    contentType: "Long-form buyer guide (>2,500 words)",
    length: "~2,800 words",
    potentialSources: ["Retail sourcing publications", "Supply chain magazines"],
    authorityPotential: "High",
    status: "Published",
    priority: "high",
  },
  {
    name: "Factory Audit Checklist",
    audience: "Retail sourcing directors, private-label brand founders",
    intent: "Informational",
    theme: "India factory audit protocol",
    url: "/services/factory-audit-india",
    contentType: "Service page + downloadable checklist framework",
    length: "~2,000 words",
    potentialSources: ["Trade associations", "Retail buyer resources"],
    authorityPotential: "High",
    status: "Published",
    priority: "high",
  },
  {
    name: "Supplier Verification Checklist",
    audience: "Importers, buying-office decision makers",
    intent: "Informational · Commercial",
    theme: "India supplier due-diligence",
    url: "/trends/verify-indian-supplier-due-diligence",
    contentType: "Buyer guide (5 verification layers)",
    length: "~2,700 words",
    potentialSources: ["Import/export publications", "Business research platforms"],
    authorityPotential: "High",
    status: "Published",
    priority: "high",
  },
  {
    name: "Quality Inspection Guide (India)",
    audience: "QA/QC directors, retail sourcing teams",
    intent: "Informational · Commercial",
    theme: "AQL 2.5 · PSI · DPI · IPC in India",
    url: "/services/quality-inspection-india",
    contentType: "Service page with inspection protocols",
    length: "~1,900 words",
    potentialSources: ["Supply chain publications", "Retail industry blogs"],
    authorityPotential: "Medium",
    status: "Published",
    priority: "medium",
  },
  {
    name: "Furniture Sourcing from India Guide",
    audience: "Furniture retailers, hospitality FF&E buyers",
    intent: "Commercial",
    theme: "India furniture manufacturing (Jodhpur, Saharanpur)",
    url: "/services/furniture-sourcing-india",
    contentType: "Vertical hub + case studies",
    length: "~1,800 words",
    potentialSources: ["Furniture trade publications", "Hospitality industry sites"],
    authorityPotential: "Medium",
    status: "Published",
    priority: "medium",
  },
  {
    name: "Lighting Sourcing from India Guide",
    audience: "Lighting brands, hospitality specifiers",
    intent: "Commercial",
    theme: "India lighting manufacturing (Moradabad, Firozabad, Aligarh)",
    url: "/categories/lighting",
    contentType: "Category hub + case studies",
    length: "~1,800 words",
    potentialSources: ["Lighting industry publications", "Interior design magazines"],
    authorityPotential: "Medium",
    status: "Published",
    priority: "medium",
  },
  {
    name: "Home Décor Manufacturing in India Guide",
    audience: "Home décor retailers, DTC lifestyle brands",
    intent: "Commercial",
    theme: "India home décor sourcing across 15 clusters",
    url: "/services/home-decor-sourcing-india",
    contentType: "Service page + related clusters",
    length: "~1,900 words",
    potentialSources: ["Retail publications", "Home décor trade sites"],
    authorityPotential: "Medium",
    status: "Published",
    priority: "medium",
  },
  {
    name: "India vs China Sourcing Comparison",
    audience: "Retail buyers under Section-301 tariff pressure",
    intent: "Informational · Commercial",
    theme: "India vs China sourcing (2026 tariff context)",
    url: "/trends/india-vs-china-sourcing-comparison",
    contentType: "Long-form comparison article",
    length: "~2,500 words",
    potentialSources: ["Trade publications", "Supply chain research", "News media"],
    authorityPotential: "High",
    status: "Published",
    priority: "high",
  },
  {
    name: "Importer's Guide to Working with Indian Manufacturers",
    audience: "First-time India buyers, US/UK/EU importers",
    intent: "Informational",
    theme: "India buying-office / agent engagement model",
    url: "/ask",
    contentType: "AI-search FAQ knowledge base",
    length: "27 answer-first Q&As",
    potentialSources: ["Trade publications", "AI search engines", "Business research platforms"],
    authorityPotential: "High",
    status: "Published",
    priority: "high",
  },
  {
    name: "Annual India Manufacturing Report",
    audience: "Sourcing directors, trade press, industry analysts",
    intent: "Informational",
    theme: "India export capacity, cluster growth, category shifts",
    url: null,
    contentType: "Annual data report (planned)",
    length: "~5,000 words + charts",
    potentialSources: ["Trade press", "Research platforms", "Industry associations"],
    authorityPotential: "Very High",
    status: "Planned",
    priority: "long-term",
  },
  {
    name: "Manufacturing Cluster Data Report",
    audience: "Retail sourcing teams, supply chain researchers",
    intent: "Informational",
    theme: "GI-tagged crafts, cluster capacity, export volumes",
    url: null,
    contentType: "Original data compilation (planned)",
    length: "~4,000 words + tables",
    potentialSources: ["GI Registry", "DGFT data", "Cluster associations"],
    authorityPotential: "High",
    status: "Planned",
    priority: "long-term",
  },
];

export const LINK_QUALITY_FRAMEWORK = [
  { criterion: "Relevance", weight: "High", note: "Is the linking site topically relevant to India sourcing, retail, home décor, furniture, lighting, or supply chain?" },
  { criterion: "Editorial Quality", weight: "High", note: "Real editors, real writers, real audience — not spun content, not auto-generated." },
  { criterion: "Industry Authority", weight: "High", note: "Recognised in the industry — trade publications, associations, established research platforms." },
  { criterion: "Real Audience", weight: "High", note: "Actual human readership. Traffic quality > raw traffic volume." },
  { criterion: "Geographic Relevance", weight: "Medium", note: "US / UK / EU / Middle East publications reach our target buyer base." },
  { criterion: "Traffic Quality", weight: "Medium", note: "Retail decision-makers, importers, sourcing professionals — not casual traffic." },
  { criterion: "Link Placement", weight: "Medium", note: "Contextual editorial link inside body copy, not a footer link farm or a resource dump." },
  { criterion: "Natural Anchor Context", weight: "High", note: "Anchor text reads naturally in-sentence. No exact-match keyword stuffing." },
];

export const ANCHOR_TEXT_STRATEGY = {
  brand: ["Asia Sourcing", "Asia Sourcing team"],
  brandContext: ["Asia Sourcing India sourcing team", "Asia Sourcing's Moradabad office"],
  natural: ["learn more", "their sourcing guide", "factory verification process", "India sourcing specialists", "read the full guide"],
  topical: [
    "sourcing furniture from India",
    "supplier verification in India",
    "factory audit services",
    "India sourcing agent",
    "quality inspection support",
  ],
  avoid: [
    "excessive exact-match anchors",
    "repeated commercial keyword anchors",
    "unnatural keyword-heavy anchors",
    "identical anchor text across many links",
  ],
};

export const OUTREACH_CATEGORIES = [
  { name: "Trade & Industry Publications", why: "Direct topical relevance; editorial links carry high trust.", how: "Expert commentary, guest research contribution, category analysis." },
  { name: "Indian Export & Manufacturing Ecosystem", why: "Establishes local credibility and cluster authority.", how: "Association membership, export council participation, chamber-of-commerce profiles." },
  { name: "International Sourcing Ecosystem", why: "Reaches actual retail buyer decision-makers.", how: "Import/export publications, retail buyer resources, supply chain research platforms." },
  { name: "Educational & Research Sources", why: "Durable authority signals; slow but high-value.", how: "Business school case studies, supply chain research, manufacturing studies." },
  { name: "Events & Trade Shows", why: "In-person credibility that spills into digital mentions.", how: "Exhibitor profiles, speaker profiles, event partnerships, industry interviews." },
];

export const AUTHORITY_ROADMAP = [
  { phase: "Phase A — Foundation", items: ["Create high-value buyer guides (28 published)", "Create cluster guides (15 published)", "Improve case studies (20 published)", "Publish expert insights (28 trends articles published)"] },
  { phase: "Phase B — Industry Recognition", items: ["Trade publication contributions", "Association profiles", "Trade show participation", "Expert interviews"] },
  { phase: "Phase C — Data Authority", items: ["Annual India sourcing reports", "Manufacturing insights", "Supplier risk reports", "Industry benchmarks"] },
  { phase: "Phase D — Digital Authority", items: ["Natural editorial mentions", "Expert citations", "Podcast/interview appearances", "Industry resource references"] },
];

export const FORBIDDEN_METHODS = [
  "Paid spam backlinks",
  "Private Blog Networks (PBNs)",
  "Automated backlink generators",
  "Bulk directory submissions",
  "Forum comment spam",
  "Fake guest posts",
  "Link exchanges at scale",
  "Hidden links",
  "Keyword-stuffed anchor campaigns",
  "Low-quality press release syndication",
];
